import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPhoneInput, normalizePhoneForDial } from '../../utils/format-phone.util';
import styles from './ManualDialPanel.module.css';

interface ManualDialPanelProps {
  onDial: (phoneNumber: string) => Promise<void>;
}

export function ManualDialPanel({ onDial }: ManualDialPanelProps) {
  const { t } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isDialing, setIsDialing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dialNumber = normalizePhoneForDial(phoneNumber);
  const canDial = Boolean(dialNumber) && !isDialing;

  async function handleDial() {
    if (!canDial) {
      return;
    }

    setError(null);
    setIsDialing(true);
    try {
      await onDial(dialNumber);
      setPhoneNumber('');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('agent:manualDial.error'));
    } finally {
      setIsDialing(false);
    }
  }

  return (
    <div className={styles.panel}>
      <label className={styles.label} htmlFor="manual-dial-number">
        {t('agent:manualDial.label')}
      </label>
      <input
        id="manual-dial-number"
        type="tel"
        className={styles.field}
        value={phoneNumber}
        onChange={(e) => {
          setPhoneNumber(formatPhoneInput(e.target.value));
          if (error) {
            setError(null);
          }
        }}
        placeholder={t('agent:manualDial.placeholder')}
        disabled={isDialing}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && canDial) {
            void handleDial();
          }
        }}
      />
      {error && <p className={styles.error}>{error}</p>}
      <button
        type="button"
        className={styles.dialButton}
        onClick={() => void handleDial()}
        disabled={!canDial}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
          <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" />
        </svg>
        {isDialing ? t('agent:manualDial.dialing') : t('agent:manualDial.action')}
      </button>
    </div>
  );
}
